/** DSD 워크북 - IXD 값 채우기 본문: 기간별 메타/표 행을 좌우가 공유하는 grid로 정렬한다. */

import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowDown,
  ArrowRight,
  Ban,
  Check,
  CheckCheck,
  CircleHelp,
  Loader2,
  TriangleAlert,
  X,
} from "lucide-react";

import type { IxdChunk, IxdDlView, PeriodType } from "@/apis/ixd-editor/types";
import type { EditableTableItemsV2 } from "@/apis/editable-preview/types";
import type { WorkbookMatchTable } from "@/apis/ixd-value-sync/types";
import type { ReportQuarter } from "@/apis/workbook/types";
import {
  IxdPreviewRenderer,
  type IxdCellNotice,
  type IxdPreviewRendererRef,
  type ModifiedCellDisplayValues,
} from "@/components/ixd-preview";
import {
  TableUnitCaption,
  toTableUnitCaptionProps,
} from "@/components/common/table-unit-caption";
import {
  type CellIdentityLocation,
  buildTableLabelMap,
} from "@/utils/ixd-cell-model";
import type { useIxdEditSession } from "@/hooks/ixd-edit-session";
import { useRawDsdTableSelection } from "@/hooks/use-raw-dsd-table-selection";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import { Divider } from "@/toggle-ui-kit/basic/divider";
import { Switch } from "@/toggle-ui-kit/control/switch";
import { TableScrollArea } from "@/toggle-ui-kit/table-scroll-area";
import { getPeriodLabel } from "@/utils/ixd-editor/period-labels";
import {
  getMixedScaleForeignCurrencies,
  getMixedScaleLineItemRows,
  getMixedScaleWarningText,
  mergeMixedScaleForeignCurrencies,
  pickLineItemUnitsInRows,
} from "@/utils/ixd-editor/unit-conversion";
import { getDisplayPeriodOrder } from "@/utils/ixd-editor/period-order";
import { isFinancialStatementChunkTitle } from "@/utils/ixd-editor/chunk-kind";
import { withChunkNoteNumber } from "@/utils/ixd-editor/chunk-note-number";
import { logIxdEditorAction } from "@/utils/usage-log";
import {
  MatchedTableEmpty,
  MatchedTablePeriodView,
} from "./matched-table-view";
import {
  CompareTargetProvider,
  useCompareTarget,
} from "./compare-target-context";
import { WorkbookAutoSumToggle } from "./workbook-auto-sum-toggle";
import { WorkbookCellNoticeProvider } from "@/components/table-preview/table/workbook-cell-notice-context";
import {
  INITIAL_PLAN_COVERAGE,
  PasteMatchButton,
  type PasteMatchPlanCoverage,
  type PasteMatchResult,
} from "./paste-match-button";
import {
  appendPasteFeedback,
  getActivePasteFeedback,
  getResolvedFailedCellUniqueIds,
  markUnitNoticeOpened,
  releaseApplyLock,
  trimPasteFeedbackHistory,
  type PasteFeedback,
  type PasteFeedbackHistory,
} from "./paste-feedback";
import {
  buildPlanCellNotices,
  pickUnitNoticeIdentityKey,
} from "./plan-cell-notices";
import type { PlanAvailability } from "./plan-value-index";
import {
  buildPlanTableDiagnostics,
  summarizeFailures,
} from "./plan-table-diagnostics";
import { UnappliedValuesBadge } from "./unapplied-values-badge";
import { useWorkbookCellNotices } from "./use-workbook-cell-notices";
import {
  buildCompareRows,
  buildRowKey,
  type CompareRow,
  type MatchedWorkbookTable,
} from "./table-matching";
import {
  attrEqualsSelector,
  lineItemHeaderSelector,
} from "@/utils/ixd-dsd-compare/cell-id-selector";
import {
  getWorkbookIxdPeriodEndNotice,
  type PeriodEndNotice,
  PeriodEndNoticeKind,
} from "./period-end-mismatch";
import { getWorkbookPeriodLabel } from "./workbook-period-label";
import {
  getThreeMonthAccComparison,
  getThreeMonthAccMismatch,
} from "./three-month-acc-mismatch";
import {
  getTaggedPeriodMismatch,
  getTaggedPeriodMismatchAction,
  getTaggedPeriodMismatchMessage,
} from "./tagged-period-mismatch";
import { getWorkbookIxdUnitMismatches } from "./unit-mismatch";
import {
  useVirtualizedCompareRow,
  type CompareRowIntersectionHandler,
  type ObserveCompareRow,
} from "./use-virtualized-compare-row";

type EditSession = ReturnType<typeof useIxdEditSession>;

// 비교 행으로 전달할 세션 필드를 이 타입에서 제한한다. 자식이 새 필드를 사용하면
// 이 목록도 함께 수정해야 컴파일되므로, 아래 memo 비교에서 필드 누락이 생기지 않는다.
const COMPARE_TABLE_SESSION_KEYS = [
  "cellChanges",
  "changeOrigins",
  "cellIdentityByNormalizedCellId",
  "identityChanges",
  "identityLocationsByKey",
  "chunkFsTypeById",
  "setCellValue",
  "setCellValues",
  "setAppliedCellValues",
  "pushUndoMarker",
  "showRawValueInEdit",
  "undoCount",
] as const satisfies readonly (keyof EditSession)[];

/** 비교 화면이 실제로 읽는 편집 세션 — 전체 EditSession 을 받지 않는다 */
export type CompareTableSession = Pick<
  EditSession,
  (typeof COMPARE_TABLE_SESSION_KEYS)[number]
>;

// 좌우 패널이 각각 독립 카드로 보이되, 기간별 행 높이는 공유 grid로 정렬한다.
// 카드 테두리는 패널 셀들이 나눠 그린다 (첫 행 rounded-t/border-t, 마지막 행 rounded-b/border-b).
//
// "DSD워크북 보기"를 끄면 워크북 칸을 렌더에서 빼고 한 칸 grid로 바꾼다 — IXD가 전폭을 쓴다.
const compareCardClass = (showWorkbook: boolean) =>
  showWorkbook
    ? "grid grid-cols-[minmax(0,1fr)_0.5rem_minmax(0,1fr)]"
    : "grid grid-cols-1";

// 청크/표 제목 타이포그래피. DSD 원본보기·합계 자동계산 토글 라벨과 같은 크기로 맞춘다.
const CHUNK_TITLE_TEXT_CLASS = "label-2-medium text-label-normal";

// Figma(7145:461461) 기준 좌 = 편집 대상 IXD, 우 = 값을 가져올 DSD 워크북.
const IXD_COL = 1;
const WORKBOOK_COL = 3;

// ponytail: 패널 1칸 너비. compareCardClass의 거터(0.5rem)와 함께 움직인다.
const COMPARE_PANEL_WIDTH = "calc(50% - 0.25rem)";
// 워크북 칸을 빼면 IXD 카드가 행 전체를 쓴다.
const ixdPanelWidth = (showWorkbook: boolean) =>
  showWorkbook ? COMPARE_PANEL_WIDTH : "100%";

// 카드 옆면. 위·아래 테두리와 둥근 모서리는 카드의 첫/마지막 블록만 그린다(cardEdge).
const PANEL_CARD_CLASS = "border-x border-line-normal bg-background-base";

/**
 * 블록이 카드의 어느 부분인지.
 *
 * Figma(7145:461461)에서 한 열은 **카드 하나**이고, 표 블록들은 그 안에서 12px 띠
 * (Basic/Divider thick)로만 나뉜다 — 가운데 블록에는 둥근 모서리도 위아래 테두리도 없다.
 */
type CardEdge = "only" | "top" | "middle" | "bottom";
const isCardTop = (edge: CardEdge) => edge === "only" || edge === "top";
const isCardBottom = (edge: CardEdge) => edge === "only" || edge === "bottom";
const cardEdgeAt = (index: number, first: number, last: number): CardEdge =>
  first === last
    ? "only"
    : index === first
      ? "top"
      : index === last
        ? "bottom"
        : "middle";

/** 카드 안에서 블록을 가르는 띠. 열마다 하나씩 — 두 열 사이 거터는 비워 둔다. */
const CompareCardDivider = ({ showWorkbook }: { showWorkbook: boolean }) => (
  <div className={compareCardClass(showWorkbook)} aria-hidden>
    <Divider
      variant="thick"
      className="border-x border-line-normal"
      style={{ gridColumn: IXD_COL }}
    />
    {showWorkbook && (
      <Divider
        variant="thick"
        className="border-x border-line-normal"
        style={{ gridColumn: WORKBOOK_COL }}
      />
    )}
  </div>
);

// 비교 행은 기간별 IXD renderer와 표 복사 observer를 함께 마운트한다. 한 번 보인 행을
// 계속 유지하면 스크롤 깊이에 비례해 document listener·observer·DOM이 누적된다.
// 새로 그리는 건 아래쪽 다음 행만. 목적지 위쪽 행이 점프 직후 뒤늦게 마운트되면
// 높이 변화가 현재 목표를 아래로 밀기 때문에 top overscan은 두지 않는다.
const COMPARE_ROW_MOUNT_MARGIN = "0px 0px 600px 0px";
// 대신 화면을 벗어난 행을 곧바로 버리지 않는다. 위로 스크롤할 때 방금 지나친 행이
// 살아 있어야 다시 그리는 지연(깜빡임)이 없다. 이미 실측된 높이를 그대로 유지하는
// 것이라 점프 시 목표가 밀리지도 않는다.
// ponytail: 유지 폭은 고정값. 행 누적이 문제가 되면 화면 높이 배수로 바꾼다.
const COMPARE_ROW_KEEP_MARGIN = "2400px 0px 2400px 0px";

/** 배율 혼재 헤더 표시 CSS 를 이 비교 행의 IXD 표로 한정하는 범위 속성 */
const MIXED_SCALE_ROW_SCOPE_ATTR = "data-mixed-scale-row-scope";

/** 블록 제목 칸 — 표 블록과 문장 블록이 같은 모양을 쓰도록 한 곳에 둔다 */
const BLOCK_TITLE_CELL_CLASS =
  "relative flex min-h-14 items-center gap-2 px-5 py-3";

const PANEL_SECTION_CLASS =
  "min-w-0 border-x border-line-normal bg-background-base";

const MissingTableMatchAlert = ({
  sourceName,
}: {
  sourceName: "DSD 워크북" | "IXD";
}) => (
  <div
    role="alert"
    className="mb-4 flex items-center gap-2 rounded-[8px] bg-red-100 px-3 py-2 text-red-500"
  >
    <TriangleAlert className="size-4 shrink-0" />
    <span className="label-2-medium">
      {sourceName === "IXD"
        ? "매칭되는 표를 찾지 못했습니다. IXD 파일을 확인해주세요."
        : "매칭되는 표를 찾지 못했습니다. DSD 워크북 원본 내용을 확인해주세요."}
    </span>
  </div>
);

export interface ReconstructedDsdTable {
  tableId: string;
  tableName: string;
  html: string;
}

const DsdViewToggle = ({
  active,
  onChange,
}: {
  active: boolean;
  onChange: (active: boolean) => void;
}) => (
  <div className="flex shrink-0 items-center gap-2">
    <span className="label-2-medium text-label-normal">DSD 원본보기</span>
    <Switch
      size={24}
      color="blue"
      active={active}
      onChange={onChange}
      aria-label="DSD 원본보기"
      icon={
        active ? (
          <Check className="size-full text-primary-color" strokeWidth={3.5} />
        ) : undefined
      }
    />
  </div>
);

const ReconstructedDsdView = ({
  tables,
  isLoading,
  hasError,
  showMissingMatchAlert,
}: {
  tables: ReconstructedDsdTable[];
  isLoading: boolean;
  hasError: boolean;
  showMissingMatchAlert: boolean;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // 주입된 DSD 원본 표에 셀 드래그 선택 + Ctrl+C 복사를 붙인다(다른 DSD 표와 동일 동작).
  useRawDsdTableSelection(containerRef, "workbook-match-dsd-selection-style");

  return (
    <TableScrollArea className="min-h-[160px]">
      <div
        ref={containerRef}
        className="flex min-h-[160px] min-w-max flex-col p-5"
      >
        {showMissingMatchAlert && (
          <MissingTableMatchAlert sourceName="DSD 워크북" />
        )}
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center gap-2 text-label-alternative">
            <Loader2 className="size-4 shrink-0 animate-spin" />
            <span className="body-2-normal-regular">
              DSD 원본을 불러오는 중...
            </span>
          </div>
        ) : hasError ? (
          <div className="flex flex-1 items-center justify-center text-center body-2-normal-regular text-label-alternative">
            DSD 원본을 불러오지 못했습니다. 잠시 후 다시 시도해주세요.
          </div>
        ) : tables.length === 0 ? (
          <div className="flex flex-1 items-center justify-center text-center body-2-normal-regular text-label-alternative">
            이 대목차에 연결된 워크북 표가 없어 재구성할 수 없습니다.
          </div>
        ) : (
          <div className="flex min-w-0 flex-col gap-6">
            {tables.map((table) => (
              <section
                key={table.tableId}
                className="flex min-w-max flex-col gap-2"
              >
                <h3 className="label-1-normal-semibold text-label-normal">
                  {table.tableName}
                </h3>
                <div
                  className="reconstructed-dsd-html caption-2-regular"
                  dangerouslySetInnerHTML={{
                    __html: table.html.replaceAll("#FEFCE8", "#FFFFFF"),
                  }}
                />
              </section>
            ))}
          </div>
        )}
      </div>
    </TableScrollArea>
  );
};

/*
 * 워크북 표의 셀에 호버하면 셀 우상단에 "더블 클릭으로 값 복사" 힌트를 띄운다.
 *
 * 복사가 되는 셀이면 표 판정과 무관하게 항상 띄운다 — 이 힌트는 "이 표가 실패했다"는
 * 신호가 아니라 "이 셀은 더블 클릭으로 복사된다"는 조작 안내다. 실패한 표에서만 켜면
 * 복사는 되는데 방법을 알 수 없는 셀이 대부분이 된다.
 *
 * 힌트 말풍선 자체는 표기 전용(pointer-events:none)이라 CSS만으로 처리하고, 실제 복사는
 * td의 onDoubleClick(table-preview/table/render-table.tsx `copyCellValue`)이 한다.
 * 그래서 선택자를 복사 가능 조건에 맞춰 둔다 — th는 핸들러를 받지 않으므로 td만 잡고,
 * 값이 빈 셀은 copyCellValue가 `!value`에서 바로 return하므로 빼야 한다. td가 값이
 * 비어도 div/span을 자식으로 갖기 때문에 `:empty`로는 걸러지지 않아, 렌더러가 값을
 * 그대로 싣는 data-display-value가 빈 것을 뺀다.
 */
const COPY_HINT_CSS = `
[data-copy-hint] td{position:relative;}
[data-copy-hint] td[data-display-value]:not([data-display-value=""]):hover::after{
  content:"더블 클릭으로 값 복사";
  position:absolute;top:0;right:0;transform:translateY(calc(-100% - 4px));
  z-index:10;pointer-events:none;white-space:nowrap;
  border-radius:2px;border:1px solid var(--primary-color);
  background-color:var(--primary-color);color:#fff;
  padding:2px 4px;font-size:11px;font-weight:500;line-height:14px;
}`;

const PERIOD_LABEL_ROW_CLASS = "flex min-h-6 items-center";

const IxdPeriodLabel = ({
  label,
  notice,
}: {
  label: string;
  /** getWorkbookIxdPeriodEndNotice 결과 */
  notice: PeriodEndNotice | null;
}) => {
  if (!notice) {
    return <span className="caption-2-medium text-label-normal">{label}</span>;
  }
  const isMismatch = notice.kind === PeriodEndNoticeKind.MISMATCH;
  const Icon = isMismatch ? TriangleAlert : CircleHelp;
  return (
    <Tooltip label={notice.message} side="top">
      <span
        role="status"
        aria-label={`${label}. ${notice.message}`}
        className={cn(
          "flex w-fit items-center gap-1",
          isMismatch ? "text-status-caution" : "text-label-normal",
        )}
      >
        <span className="caption-2-medium">{label}</span>
        <Icon
          className={cn(
            "size-3.5 shrink-0",
            !isMismatch && "text-label-alternative",
          )}
        />
      </span>
    </Tooltip>
  );
};

/**
 * '3개월/누적 사용' 여부. IXD 편집기 비교표시설정의 체크박스와 같은 이름으로 워크북 칸과
 * IXD 칸에 나란히 그려, 기간 라벨처럼 양쪽을 보고 다른 곳을 찾게 한다. 체크박스 테두리는
 * 누르는 컨트롤처럼 보여 ✓/✕ 기호만 쓴다. 워크북이 기준이라 어긋나면 IXD 쪽만 주의 색과
 * 경고 아이콘을 달고, 맞추는 방법은 기간 라벨과 같이 툴팁이 말한다.
 */
const ThreeMonthAccStatus = ({
  used,
  mismatch = null,
}: {
  used: boolean;
  /** getThreeMonthAccMismatch 결과 — IXD 칸에만 넘긴다 */
  mismatch?: string | null;
}) => {
  const StateIcon = used ? Check : X;
  const stateText = `3개월/누적 ${used ? "사용" : "미사용"}`;
  const status = (
    <span
      role="status"
      aria-label={mismatch ? `${stateText}. ${mismatch}` : stateText}
      className={cn(
        "flex w-fit items-center gap-1",
        mismatch ? "text-status-caution" : "text-label-alternative",
      )}
    >
      {/* lucide 아이콘은 안쪽 여백이 있어 gap-1 그대로면 기호와 글자가 떨어져 보인다 —
          기호 쪽만 2px 당겨 한 덩어리로 읽히게 한다(뒤의 ⚠ 간격은 그대로). */}
      <StateIcon className="-mr-0.5 size-3.5 shrink-0" />
      <span className="caption-2-medium">3개월/누적</span>
      {mismatch && <TriangleAlert className="size-3.5 shrink-0" />}
    </span>
  );
  if (!mismatch) return status;
  return (
    <Tooltip label={mismatch} side="top">
      {status}
    </Tooltip>
  );
};

/**
 * plan 자체를 쓸 수 없는 상태의 안내.
 *
 * 표 판정과 무관한 문제라 status가 무엇이든 알린다 — 감추면 자동 반영이 통째로
 * 죽은 것을 사용자가 알 방법이 없다.
 *
 * 개별 셀이 안 채워지는 것은 여기서 다루지 않는다. 그건 표 배지와 진단 목록이
 * 더 정확한 수(미반영 건수)로 이미 말하고, 미커버 셀 수를 따로 세어 알리면
 * 같은 얘기를 두 번 하면서 숫자만 어긋난다.
 */
const PlanUnavailableAlert = ({
  coverage,
}: {
  coverage: PasteMatchPlanCoverage;
}) => (
  <div
    role="alert"
    className="mb-4 flex items-center gap-2 rounded-[8px] bg-red-100 px-3 py-2 text-red-500"
  >
    <TriangleAlert className="size-4 shrink-0" />
    <span className="label-2-medium">
      {coverage.status === "join-failed"
        ? "DSD 워크북 값과 IXD 표의 정보가 맞지 않습니다."
        : "값 동기화 정보를 불러오지 못해 자동 반영을 사용할 수 없습니다."}{" "}
      셀을 클릭해 값을 복사해 직접 붙여넣어 주세요.
    </span>
  </div>
);

const PasteFeedbackBadge = ({ hasFailures }: { hasFailures: boolean }) =>
  hasFailures ? (
    <span
      role="status"
      className="flex items-center gap-1.5 whitespace-nowrap rounded-[6px] bg-yellow-100 px-2 py-1 caption-2-medium text-orange-500"
    >
      <TriangleAlert className="size-4 shrink-0" />
      일부 값이 반영되지 않았습니다. 누락된 항목을 확인해주세요.
    </span>
  ) : (
    <span
      role="status"
      className="whitespace-nowrap rounded-[6px] bg-green-100 px-2 py-1 caption-2-medium text-status-positive"
    >
      반영 완료
    </span>
  );

const TablePlaceholder = ({
  icon,
  label,
}: {
  icon: React.ReactNode;
  label: string;
}) => (
  <div className="flex h-full min-h-[160px] flex-col items-center justify-center gap-2.5 rounded-[10px] border-2 border-dashed border-line-normal bg-background-neutral-alternative">
    {icon}
    <p className="label-2-medium text-center text-label-alternative">{label}</p>
  </div>
);

/**
 * 워크북에는 태깅된 기간인데 IXD 표에 그 기간이 없다 — 워크북 칸의 값을 받을 자리가 없다.
 * "매칭된 표 없음"처럼 칸은 비워 두고, 맞추는 방법은 툴팁이 말한다.
 */
const MissingIxdPeriodPlaceholder = ({
  periodLabel,
  action,
}: {
  periodLabel: string;
  /** getTaggedPeriodMismatchAction 결과 */
  action: string;
}) => (
  <Tooltip label={action} side="top">
    {/* Tooltip 트리거는 ref 를 받는 element 여야 해서 칸 크기를 그대로 잇는 div 로 감싼다 */}
    <div
      role="status"
      aria-label={`IXD에 ${periodLabel} 표 없음. ${action}`}
      className="h-full"
    >
      <TablePlaceholder
        icon={<TriangleAlert className="size-5 text-status-caution" />}
        label={`IXD에 ${periodLabel} 표 없음`}
      />
    </div>
  </Tooltip>
);

/**
 * IXD 표에는 있는데 DSD 워크북에는 태깅이 없는 기간의 IXD 라벨 옆 표시. 워크북이 기준이라
 * 이 기간은 IXD에서 빼야 한다 — 빼는 방법은 3개월/누적 표시처럼 툴팁이 말한다.
 */
const IxdExtraPeriodStatus = ({ action }: { action: string }) => (
  <Tooltip label={action} side="top">
    <span
      role="status"
      aria-label={`DSD 워크북에 없는 기간. ${action}`}
      className="flex w-fit items-center gap-1 text-status-caution"
    >
      <span className="caption-2-medium">DSD 워크북에 없음</span>
      <TriangleAlert className="size-3.5 shrink-0" />
    </span>
  </Tooltip>
);

const MissingTableMatchPlaceholder = () => (
  <TablePlaceholder
    icon={<Ban className="size-5 text-red-500" />}
    label="매칭된 표 없음"
  />
);

/**
 * 매칭에 실패했을 때 사용자가 DSD 원본에서 할 일 (Figma 8873:793221).
 *
 * 매칭이 없는 칸은 대부분 디테일 태깅 케이스다 — 원인을 짚는 확인 목록 대신
 * 바로 다음 행동(원본 보며 직접 태깅)을 먼저 말한다.
 */
const MISSING_MATCH_GUIDE_ITEMS = [
  "DSD 원본을 보면서 직접 디테일 태깅을 진행해주세요.",
  "표가 여러 개로 나뉘었거나 하나로 합쳐졌는지 확인해주세요.",
];

/**
 * 주석 목차의 워크북 칸에 매칭된 표가 없을 때의 안내 (Figma 8873:793223 "Drop Zone").
 *
 * 사유를 칸 위의 빨간 줄로 따로 띄우지 않고 빈 칸 안에서 말한다 — 눈이 이미 가 있는
 * 자리라 읽히고, 바로 아래 "DSD 원본 보기"로 다음 행동까지 이어진다.
 *
 * 세로 가운데가 아니라 위쪽에 붙인다(재무제표 본문 안내와 같은 이유) — 칸 높이는
 * 왼쪽 IXD 표를 따라가 화면보다 길어질 수 있고, 가운데 정렬이면 안내가 스크롤
 * 아래로 밀려 "빈 화면"이 된다.
 *
 * 값은 Figma 변수를 그대로 옮겼다: 제목 Caption 1/SemiBold + Label/Normal, 부제
 * Caption 2/Regular + neutral/500, 안내 목록 Caption 2/Medium + neutral/600 위에
 * gray/200 + radius 4 + padding 8/16, 칸 Background/Gray/Alternative + Line/Normal
 * 2px 점선 + radius 8 + padding 80/16.
 *
 * 안내 목록만 제목 칸(266px)보다 넓다 — Figma에서 308px로 양옆 21px씩 비어져 나와
 * 두 줄이 각각 한 줄로 떨어진다. 폭을 266에 맞추면 네 줄로 접혀 안내가 뭉개진다.
 */
const MissingTableMatchGuide = ({
  onShowDsdSource,
}: {
  onShowDsdSource: () => void;
}) => (
  <div className="flex h-full min-h-[160px] flex-col items-center gap-4 rounded-[8px] border-2 border-dashed border-line-normal bg-background-gray-alternative px-4 py-20">
    <div className="flex w-full flex-col items-center gap-3">
      <div className="flex w-[266px] max-w-full flex-col items-center gap-1">
        <p className="caption-1-semibold text-center text-label-normal">
          매칭되는 표를 찾지 못했습니다.
        </p>
        <p className="caption-2-regular text-center text-neutral-500">
          DSD 원본 내용을 확인해보세요.
        </p>
      </div>
      {/* 제목 칸(266px)보다 넓게, 문구 길이만큼 늘어난다 (Figma 8873:795089 = 308px). */}
      <ul className="w-max max-w-full list-disc space-y-1 rounded-[4px] bg-gray-200 py-2 pl-6 pr-4 caption-2-medium text-neutral-600">
        {MISSING_MATCH_GUIDE_ITEMS.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
    <Button
      variant="outlined"
      color="secondary"
      size="small"
      rightIcon={<ArrowRight />}
      onClick={onShowDsdSource}
    >
      DSD 원본 보기
    </Button>
  </div>
);

/**
 * 재무제표 본문 목차의 워크북 칸 (Figma 8873:792676 "Drop Zone").
 *
 * 본문은 매칭할 워크북 표가 애초에 없어 값 반영도 DSD 원본보기도 쓸 수 없다. 빈 칸만
 * 두면 고장으로 읽히므로 사유와 다음 행동(주석으로 이동)을 함께 적는다.
 *
 * 세로 가운데가 아니라 위쪽에 붙인다(Figma: primaryAxis=MIN, padding 64/16) — 칸
 * 높이는 오른쪽 IXD 표를 따라가 화면보다 길어질 수 있고, 가운데 정렬이면 안내가
 * 스크롤 아래로 밀려 "빈 화면"이 된다.
 *
 * 값은 Figma 변수를 그대로 옮겼다: 아이콘 orange/500(#ff6900, 16px) — 팔레트에
 * 대응 토큰이 없어 Switch 의 orange 와 같이 값으로 쓴다, 제목 Caption 1/SemiBold +
 * Label/Normal, 본문 Caption 2/Regular + neutral/500, 칸 Background/Gray/Alternative
 * + Line/Normal 2px 점선 + radius 8.
 */
const FinancialStatementPlaceholder = ({
  onGoToFirstNote,
}: {
  /** 주석 목차가 하나도 없으면 undefined — 갈 곳이 없으면 권하지 않는다 */
  onGoToFirstNote?: () => void;
}) => (
  <div className="flex h-full min-h-[160px] flex-col items-center gap-2 rounded-[8px] border-2 border-dashed border-line-normal bg-background-gray-alternative px-4 py-16">
    <div className="flex flex-col items-center gap-2">
      <TriangleAlert className="size-4 text-[#ff6900]" />
      <p className="caption-1-semibold text-center text-label-normal">
        재무제표 본문은 값 반영을 지원하지 않습니다.
      </p>
      {/* Figma 는 한 텍스트 노드의 빈 줄로 문단을 띄운다 — 줄바꿈 두 번이 같은 간격 */}
      <p className="caption-2-regular text-center text-neutral-500">
        본문 영역에서는 워크북 값을 반영하거나 표를 미리 볼 수 없어요.
        <br />
        주석에서는 값을 반영하고 결과를 바로 확인할 수 있어요.
        {onGoToFirstNote && (
          <>
            <br />
            <br />
            아래 주석 번호를 선택해 시작해 보세요.
          </>
        )}
      </p>
    </div>
    {onGoToFirstNote && (
      <Button
        variant="outlined"
        color="secondary"
        size="small"
        rightIcon={<ArrowDown />}
        onClick={onGoToFirstNote}
      >
        주석 보러 가기
      </Button>
    )}
  </div>
);

/**
 * 워크북 매칭 표가 없을 때 칸을 채우는 안내. 두 호출 지점(DSD 원본보기 자리,
 * 기간을 합친 칸)이 같은 규칙을 쓰도록 분기를 여기 한 곳에 둔다.
 *
 * 기간별로 쪼개 부르지 않는다 — 호출부가 당기·전기를 한 칸으로 합쳐 그린다.
 */
export const MissingWorkbookPlaceholder = ({
  isFinancialStatement,
  onGoToFirstNote,
  onShowDsdSource,
}: {
  isFinancialStatement: boolean;
  onGoToFirstNote?: () => void;
  /** "DSD 원본 보기" — 재무제표 본문은 재구성할 원본 자체가 없어 넘어오지 않는다 */
  onShowDsdSource?: () => void;
}) =>
  isFinancialStatement ? (
    <FinancialStatementPlaceholder onGoToFirstNote={onGoToFirstNote} />
  ) : onShowDsdSource ? (
    <MissingTableMatchGuide onShowDsdSource={onShowDsdSource} />
  ) : (
    <MissingTableMatchPlaceholder />
  );

/**
 * 표 제목 옆 "모든 항목 값 일치" (Figma 8860:784445).
 *
 * 반영불가 칩과 같은 자리를 두고 배타적이다 — 반영불가가 1건이라도 있으면
 * 모든 항목이 일치한 표가 아니므로 걷는다(칩 쪽은 0건에서 스스로 사라진다).
 * 반영 직후에는 같은 자리에 "반영 완료"가 서므로 그동안도 걷는다.
 *
 * 판정을 이 컴포넌트 안에 두어 호출부의 JSX 조건과 테스트가 한 곳을 본다.
 */
export const ValuesMatchStatus = ({
  coverageStatus,
  notAppliedCount,
  hasPasteFeedback,
}: {
  coverageStatus: PasteMatchPlanCoverage["status"];
  notAppliedCount: number;
  hasPasteFeedback: boolean;
}) => {
  if (
    coverageStatus !== "values-match" ||
    notAppliedCount !== 0 ||
    hasPasteFeedback
  ) {
    return null;
  }
  return (
    <span
      role="status"
      className="flex shrink-0 items-center gap-1 whitespace-nowrap caption-2-medium text-label-alternative"
    >
      <CheckCheck className="size-4 text-status-positive" strokeWidth={2.25} />
      모든 항목 값 일치
    </span>
  );
};

const resolveFinalPeriod = (
  periodType: PeriodType,
): "CURRENT" | "PRIOR" | null => {
  if (periodType === "CURRENT") return "CURRENT";
  if (periodType === "PREVIOUS") return "PRIOR";
  return null;
};

interface PeriodSection {
  periodType: PeriodType;
  periodLabel: string;
  /** 워크북 칸 라벨. 워크북 표가 정하는 기간(당기·전기)이 아니면 IXD 라벨 */
  workbookPeriodLabel: string;
  /**
   * IXD 표에 이 기간이 있는지. 없으면 워크북에 태깅된 기간이라 끼워 넣은 칸이다 —
   * 워크북 값은 그대로 보이고 IXD 칸은 그 기간이 없다고 알린다.
   */
  hasIxdTable: boolean;
  finalPeriod: "CURRENT" | "PRIOR" | null;
  finalTable: EditableTableItemsV2 | undefined;
}

interface CompareTableRowsProps {
  /** 비교 중인 DSD 워크북 — 화면이 preview/plan 을 받은 그 id. 안 골랐으면 빈 문자열 */
  workbookId: string;
  /** 비교 중인 워크북의 보고 기간 — 워크북 칸 기간 라벨(당반기 등)에 쓴다 */
  reportQuarter: ReportQuarter | null;
  reportId: string;
  chunks: IxdChunk[];
  matches: Map<string, MatchedWorkbookTable>;
  workbookOnly: WorkbookMatchTable[];
  workbookOnlyByChunkId: Map<string, WorkbookMatchTable[]>;
  session: CompareTableSession;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  onScroll: (container: HTMLDivElement) => void;
  onUndo: () => void;
  /** 되돌리기 등으로 특정 셀 위치에 포커스·스크롤을 요청한다. 매 요청마다 새 객체. */
  focusRequest?: {
    location: CellIdentityLocation;
    scrollBehavior?: ScrollBehavior;
  } | null;
  /** 수정 셀의 기존/수정 표시값 — 셀 옆 popover 하단에 노출한다. */
  getModifiedCellValues: (
    cell: HTMLElement,
  ) => ModifiedCellDisplayValues | null;
  /** 저장이 거절된 셀 — 표에서도 빨간 표식으로 찾을 수 있게 한다. */
  rejectedCellNotices?: ReadonlyMap<string, IxdCellNotice>;
  getReconstructedDsdTables: (chunkId: string) => ReconstructedDsdTable[];
  isDsdViewsLoading: boolean;
  hasDsdViewsError: boolean;
  onRequestDsdViews: () => void;
  /** 값 동기화 plan — 어느 셀에 어떤 값이 들어갈지의 유일한 근거 */
  plan: PlanAvailability;
  /** 재무제표 본문 안내의 "주석 보러 가기" — 주석 목차가 없으면 넘기지 않는다 */
  onGoToFirstNote?: () => void;
  /**
   * 오른쪽 DSD 워크북 열을 보여줄지 ("DSD워크북 보기" 스위치).
   *
   * IXD 표가 있는 행은 워크북 칸만 invisible 로 감춘다 — 행 높이·스크롤 위치가
   * 그대로라 다시 켤 때 화면이 튀지 않는다. 워크북만 있는 행은 IXD 칸에도 내용이
   * 없으므로 높이까지 접는다(빈 공간·불필요한 마운트 방지). 접히며 생기는 이동은
   * 호출부가 토글 직후 보고 있던 목차로 다시 스크롤해 메운다.
   */
  showWorkbook: boolean;
  /** 값이 갈린 항목에서 사용자가 고른 값 */
  /** null이면 선택 해제 */
}

interface CompareTableRowProps {
  chunk: IxdChunk;
  dlView: IxdDlView;
  dlViewIndex: number;
  match: MatchedWorkbookTable | undefined;
  session: CompareTableSession;
  pasteFeedback?: PasteFeedback;
  onPasteResult: (rowKey: string, result: PasteMatchResult) => void;
  onApplyLockRelease: (rowKey: string) => void;
  /** 반영 직후 단위 배율 알림 패널을 열었다고 반영 기록에 남긴다 */
  onUnitNoticeOpened: (rowKey: string) => void;
  plan: PlanAvailability;
  /** 워크북 표 id → 표 이름. 충돌 후보의 출처를 사람이 읽을 수 있게 한다 */
  registerRenderer: (
    key: string,
    renderer: IxdPreviewRendererRef | null,
  ) => void;
  focusIdentityLocation: (location: CellIdentityLocation) => boolean;
  getModifiedCellValues: (
    cell: HTMLElement,
  ) => ModifiedCellDisplayValues | null;
  rejectedCellNotices?: ReadonlyMap<string, IxdCellNotice>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  getReconstructedDsdTables: (chunkId: string) => ReconstructedDsdTable[];
  isDsdViewsLoading: boolean;
  hasDsdViewsError: boolean;
  onRequestDsdViews: () => void;
  showWorkbook: boolean;
  /** 이 블록이 카드의 어느 부분인지 — 둥근 모서리·위아래 테두리를 결정한다 */
  cardEdge: CardEdge;
  /** 이 목차의 첫 블록인지 — 목차 제목은 첫 블록에서만 보여준다 */
  isChunkStart: boolean;
  onGoToFirstNote?: () => void;
}

/**
 * sticky 제목 행이 실제로 상단에 붙어 있는지 감지한다. 제목 행을 top:-1px로
 * 두고 threshold 1 관찰 — 붙는 순간 1px이 컨테이너 밖으로 잘려 ratio < 1이 된다.
 */
const useStickyHeaderStuck = (
  headerRef: React.RefObject<HTMLDivElement>,
  scrollContainerRef: React.RefObject<HTMLDivElement>,
) => {
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const header = headerRef.current;
    const root = scrollContainerRef.current;
    if (!header || !root) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(entry.intersectionRatio < 1),
      { root, threshold: [1] },
    );
    observer.observe(header);
    return () => observer.disconnect();
  }, [headerRef, scrollContainerRef]);

  return isStuck;
};

const EMPTY_RESOLVED_FAILURE_IDS = new Set<string>();

/** 셀 편집 DOM 반영 뒤 실패 당시 값과 비교해 직접 처리한 실패 셀을 제외한다. */
const useResolvedPasteFailureIds = (
  pasteFeedback: PasteFeedback | undefined,
  targetContainerRef: React.RefObject<HTMLDivElement>,
  cellChanges: CompareTableSession["cellChanges"],
) => {
  const [resolvedState, setResolvedState] = useState<{
    feedback: PasteFeedback | undefined;
    ids: Set<string>;
  }>({ feedback: undefined, ids: EMPTY_RESOLVED_FAILURE_IDS });

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const targetRoot = targetContainerRef.current;
      const ids =
        pasteFeedback && targetRoot
          ? getResolvedFailedCellUniqueIds(pasteFeedback, targetRoot)
          : EMPTY_RESOLVED_FAILURE_IDS;
      setResolvedState({ feedback: pasteFeedback, ids });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [cellChanges, pasteFeedback, targetContainerRef]);

  return resolvedState.feedback === pasteFeedback
    ? resolvedState.ids
    : EMPTY_RESOLVED_FAILURE_IDS;
};

const CompareTableRow = ({
  chunk,
  dlView,
  dlViewIndex,
  match,
  session,
  pasteFeedback,
  onPasteResult,
  onApplyLockRelease,
  onUnitNoticeOpened,
  plan,
  registerRenderer,
  focusIdentityLocation,
  getModifiedCellValues,
  rejectedCellNotices,
  scrollContainerRef,
  getReconstructedDsdTables,
  isDsdViewsLoading,
  hasDsdViewsError,
  onRequestDsdViews,
  showWorkbook,
  cardEdge,
  isChunkStart,
  onGoToFirstNote,
}: CompareTableRowProps) => {
  const sourceContainerRef = useRef<HTMLDivElement>(null);
  const targetContainerRef = useRef<HTMLDivElement>(null);
  // 이 행이 그린 IXD 렌더러들 — 반영 직후 알림 셀을 스크롤 없이 선택할 때 쓴다.
  const rowRenderersRef = useRef(new Map<string, IxdPreviewRendererRef>());
  const registerRowRenderer = useCallback(
    (key: string, renderer: IxdPreviewRendererRef | null) => {
      if (renderer) rowRenderersRef.current.set(key, renderer);
      else rowRenderersRef.current.delete(key);
      registerRenderer(key, renderer);
    },
    [registerRenderer],
  );
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const isHeaderStuck = useStickyHeaderStuck(
    stickyHeaderRef,
    scrollContainerRef,
  );
  const [planCoverage, setPlanCoverage] = useState<PasteMatchPlanCoverage>(
    INITIAL_PLAN_COVERAGE,
  );
  // 태깅이 없어 자동으로 못 채우는 셀이 있으면 수동 복사 안내를 켠다. 표 구조가
  // 아니라 태깅 커버리지가 기준이다 — 구조가 같아도 태깅이 없으면 못 채운다.
  // 서버가 어떤 근거로 값을 정했는지는 표 단위 계획에만 있다. roleKey로 잇는다.
  const planDiagnostics = useMemo(() => {
    const workbookTableId =
      plan.status === "ready" ? match?.tableId : undefined;
    if (!workbookTableId || plan.status !== "ready") {
      return buildPlanTableDiagnostics(undefined);
    }
    return buildPlanTableDiagnostics(
      plan.index.tablesByWorkbookTableId.get(workbookTableId),
    );
  }, [match?.tableId, plan]);
  /*
   * 셀 위에 붙일 알림. 렌더러는 문구를 그리기만 하고 의미는 여기서 정한다.
   *
   * 자리가 겹쳐 값을 넣지 못한 셀과, 값 반영으로 단위 배율이 적용된 셀을
   * 알린다 — 값이 갈리는 항목은 이제 그냥 채우므로 알릴 것이 없다.
   */
  const cellNotices = useMemo(() => {
    const notices =
      plan.status === "ready"
        ? buildPlanCellNotices(
            plan.index.cellNoticeByIdentityKey,
            new Set(pasteFeedback?.appliedIdentityKeys),
          )
        : new Map<string, IxdCellNotice>();
    // 저장이 막힌 셀이 우선한다 — 사용자가 지금 조치해야 하는 쪽이다.
    for (const [identityKey, notice] of rejectedCellNotices ?? []) {
      notices.set(identityKey, notice);
    }
    return notices.size > 0 ? notices : undefined;
  }, [plan, rejectedCellNotices, pasteFeedback?.appliedIdentityKeys]);

  /*
   * 값 반영 직후, 단위 배율이 적용된 첫 셀을 선택해 알림 패널을 연다 — 셀을 찾아
   * 누르지 않아도 확인할 것이 있다는 걸 바로 보게 한다.
   *
   * 반영 한 번에 한 번만 연다. 열었다는 기록은 반영 기록(PasteFeedback)에 둔다 —
   * 행은 가상화로 다시 마운트되므로 행 로컬에 두면 돌아올 때마다 다시 열린다.
   * 셀 선택에 성공했을 때만 기록해, 렌더러가 아직 붙지 않았으면 다음 기회에 연다.
   */
  const rowKey = buildRowKey(chunk.id, dlViewIndex);
  useEffect(() => {
    const appliedIdentityKeys = pasteFeedback?.appliedIdentityKeys;
    const target = targetContainerRef.current;
    if (
      !appliedIdentityKeys ||
      pasteFeedback?.unitNoticeOpened ||
      !target ||
      plan.status !== "ready"
    ) {
      return;
    }
    const identityKey = pickUnitNoticeIdentityKey(
      appliedIdentityKeys,
      plan.index.cellNoticeByIdentityKey,
    );
    const location = identityKey
      ? session.identityLocationsByKey
          .get(identityKey)
          ?.find(
            ({ uid }) =>
              uid !== undefined &&
              target.querySelector(
                `#${CSS.escape(uid)},[data-cell-identity-location-uid="${CSS.escape(uid)}"]`,
              ),
          )
      : undefined;
    if (!location) return;
    // 사용자는 방금 이 표에서 반영을 눌렀다 — 화면을 움직이지 않고 선택만 한다.
    for (const renderer of rowRenderersRef.current.values()) {
      if (renderer.focusCellLocation(location, { scroll: false })) {
        onUnitNoticeOpened(rowKey);
        return;
      }
    }
  }, [
    onUnitNoticeOpened,
    pasteFeedback?.appliedIdentityKeys,
    pasteFeedback?.unitNoticeOpened,
    plan,
    rowKey,
    session.identityLocationsByKey,
  ]);

  // plan을 못 받았거나 조인이 깨진 것은 표 판정과 무관한 문제라 항상 알린다.
  const hasBlockingPlanProblem =
    planCoverage.status === "plan-error" ||
    planCoverage.status === "join-failed";
  const [showReconstructedDsd, setShowReconstructedDsd] = useState(false);
  const resolvedFailureIds = useResolvedPasteFailureIds(
    pasteFeedback,
    targetContainerRef,
    session.cellChanges,
  );
  const activeFailedCellUniqueIds =
    pasteFeedback?.failedCellUniqueIds.filter(
      (uniqueId) => !resolvedFailureIds.has(uniqueId),
    ) ?? [];
  /**
   * 반영되지 않은 워크북 셀에 붙일 사유 (주소 → 문구).
   *
   * failures는 대응 IXD 셀을 못 찾아 실패한 것이라 IXD 쪽에는 붙일 자리가 없다.
   * 워크북 셀 주소로 좌측 표의 그 셀을 짚는다 — 고쳐야 할 자리도 거기다.
   */
  const planNotAppliedSummary = useMemo(
    () => summarizeFailures(planDiagnostics),
    [planDiagnostics],
  );
  const periodTypes = useMemo(
    () =>
      getDisplayPeriodOrder(dlView.fsType).filter(
        (periodType) => (dlView.table?.[periodType]?.length ?? 0) > 0,
      ),
    [dlView.fsType, dlView.table],
  );
  /*
   * 단위 경고 — 캡션, 값 반영 버튼(주의 점·툴팁), 반영 토스트가 모두 이 값을 본다.
   *
   * 외화 배율 혼재는 캡션이 기간 섹션마다 그 섹션 행으로 판정하므로, 버튼·토스트도
   * 섹션별 판정을 모아 쓴다. 기간 사이에만 갈리는 배율을 버튼만 경고하면 어느 캡션에서도
   * 근거를 찾을 수 없다. 단위 맵은 표에 그려지는 라인아이템으로 좁힌다(pickLineItemUnitsInRows).
   */
  const { mixedScaleWarning, mixedScaleLineItemIds, unitMismatchLines } =
    useMemo(() => {
      const rowsByPeriod = periodTypes.map(
        (periodType) => dlView.table?.[periodType] ?? [],
      );
      const currencies = mergeMixedScaleForeignCurrencies(
        rowsByPeriod.map((rows) =>
          getMixedScaleForeignCurrencies(
            pickLineItemUnitsInRows(dlView.lineItemUnits, rows),
          ),
        ),
      );
      const unitMismatchLines = getWorkbookIxdUnitMismatches(
        [match?.current, match?.prior].filter(
          (table): table is EditableTableItemsV2 => !!table,
        ),
        {
          unitText: dlView.unitText,
          foreignUnits: pickLineItemUnitsInRows(
            dlView.lineItemUnits,
            rowsByPeriod.flat(),
          ),
        },
      );
      if (currencies.length === 0) {
        return {
          mixedScaleWarning: null,
          mixedScaleLineItemIds: [],
          unitMismatchLines,
        };
      }
      // 기간마다 행 헤더가 같지 않을 수 있어 전 기간 표에서 라벨을 모은다.
      const labels = new Map<string, string>();
      for (const rows of rowsByPeriod) {
        for (const [id, label] of buildTableLabelMap(rows)) {
          if (!labels.has(id)) labels.set(id, label);
        }
      }
      return {
        mixedScaleWarning: {
          text: getMixedScaleWarningText(currencies),
          rows: getMixedScaleLineItemRows(currencies, labels),
        },
        mixedScaleLineItemIds: currencies.flatMap(({ scales }) =>
          scales.flatMap(({ lineItemIds }) => lineItemIds),
        ),
        unitMismatchLines,
      };
    }, [
      periodTypes,
      match,
      dlView.table,
      dlView.unitText,
      dlView.lineItemUnits,
    ]);
  const {
    notices: workbookCellNotices,
    noticesByTagKey: workbookCellNoticesByTagKey,
    unappliedGroups: workbookUnappliedGroups,
    onNoticeClick: handleWorkbookNoticeClick,
    popover: workbookNoticePopover,
  } = useWorkbookCellNotices(planDiagnostics, match?.tableName);
  const handlePasteResult = useCallback(
    (result: PasteMatchResult) => onPasteResult(rowKey, result),
    [onPasteResult, rowKey],
  );
  const applyLock = useMemo(
    () => ({
      snapshot: pasteFeedback?.appliedCellSnapshot ?? null,
      onRelease: () => onApplyLockRelease(rowKey),
    }),
    [onApplyLockRelease, pasteFeedback?.appliedCellSnapshot, rowKey],
  );
  const failedCellCss = activeFailedCellUniqueIds
    .map(
      (uniqueId) =>
        `#${CSS.escape(uniqueId)}{background-color:rgb(254 226 226);}`,
    )
    .join("");
  /*
   * 캡션 배율 혼재 경고에 hover 하는 동안 IXD 표에서 그 라인아이템의 제목 헤더를 표시한다.
   * 모양은 셀 포커스(inset 2px 링)와 같고 색만 주의 색이다. hover 중 표가 다시 그려져도
   * 유지되도록 DOM 을 직접 칠하지 않고 이 행 범위에 CSS 를 주입한다(failedCellCss 와 같은 방식).
   */
  const [isMixedScaleWarningHovered, setIsMixedScaleWarningHovered] =
    useState(false);
  const mixedScaleRowScope = attrEqualsSelector(
    MIXED_SCALE_ROW_SCOPE_ATTR,
    rowKey,
  );
  const mixedScaleHeaderCss =
    isMixedScaleWarningHovered && mixedScaleLineItemIds.length > 0
      ? `${mixedScaleLineItemIds
          .map((id) => `${mixedScaleRowScope} ${lineItemHeaderSelector(id)}`)
          .join(
            ",",
          )}{box-shadow:inset 0 0 0 2px var(--status-caution)!important;}`
      : "";
  const { reportQuarter } = useCompareTarget();
  const periodEndNotice = getWorkbookIxdPeriodEndNotice(
    match?.isPeriodEnd,
    dlView,
  );
  const threeMonthAcc = getThreeMonthAccComparison(
    match?.isThreeMonthAcc,
    dlView.table,
  );
  const threeMonthAccMismatch = getThreeMonthAccMismatch(threeMonthAcc);
  const taggedPeriodMismatch = getTaggedPeriodMismatch(
    match?.taggedPeriods,
    periodTypes,
  );
  const taggedPeriodMismatchAction = taggedPeriodMismatch
    ? getTaggedPeriodMismatchAction(taggedPeriodMismatch)
    : null;
  const taggedPeriodMismatchMessage = getTaggedPeriodMismatchMessage(
    taggedPeriodMismatch,
    (period) =>
      getWorkbookPeriodLabel(
        period === "CURRENT" ? "CURRENT" : "PRIOR",
        reportQuarter,
        match?.isPeriodEnd,
      ),
  );
  const isFinancialStatement = isFinancialStatementChunkTitle(chunk.title);
  const isMissingWorkbookMatch = !match && !isFinancialStatement;

  // IXD 표가 있으면 워크북에만 태깅된 기간도 칸을 만든다 — 워크북은 가진 기간을 다 보여 주고,
  // IXD 칸이 그 기간이 없다고 알린다. IXD 표가 아예 없는 블록(문장 등)은 아래 분기가 따로 그린다.
  const sectionPeriodTypes = getDisplayPeriodOrder(dlView.fsType).filter(
    (periodType) =>
      periodTypes.includes(periodType) ||
      (periodTypes.length > 0 &&
        taggedPeriodMismatch?.missingInIxd.some(
          (period) => period === periodType,
        )),
  );
  const sections: PeriodSection[] = sectionPeriodTypes.map((periodType) => {
    const finalPeriod = resolveFinalPeriod(periodType);
    const periodLabel = getPeriodLabel(periodType, dlView.periodLabels);
    const finalTable =
      finalPeriod === "CURRENT"
        ? (match?.current ?? undefined)
        : finalPeriod === "PRIOR"
          ? (match?.prior ?? undefined)
          : undefined;

    return {
      periodType,
      periodLabel,
      workbookPeriodLabel: finalPeriod
        ? getWorkbookPeriodLabel(finalPeriod, reportQuarter, match?.isPeriodEnd)
        : periodLabel,
      hasIxdTable: periodTypes.includes(periodType),
      finalPeriod,
      finalTable,
    };
  });

  const reconstructedDsdTables = getReconstructedDsdTables(chunk.id);
  const handleDsdViewToggle = useCallback(
    (active: boolean) => {
      logIxdEditorAction(active ? "DSD 원본보기 켜기" : "DSD 원본보기 끄기", {
        workbookTableId: match?.tableId,
        ixdChunkId: chunk.id,
      });
      setShowReconstructedDsd(active);
      if (active) {
        onRequestDsdViews();
      } else {
        // 토글을 끄면 DOM 반영 후 경로와 무관하게 카드 상단으로 이동한다.
        requestAnimationFrame(() => {
          scrollAnchorRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        });
      }
    },
    [onRequestDsdViews, match?.tableId, chunk.id],
  );
  // 빈 칸 안내의 "DSD 원본 보기" — 스위치를 켠 것과 같은 경로를 탄다(로그·요청 포함).
  const handleShowDsdSource = useCallback(
    () => handleDsdViewToggle(true),
    [handleDsdViewToggle],
  );
  // 재무제표 본문은 매칭된 워크북 표가 없어 재구성할 원본 자체가 없다.
  // 스위치도 안내 버튼도 내보내지 않는다 — 분기 하나가 둘을 같이 정한다.
  const showDsdSource = isFinancialStatement ? undefined : handleShowDsdSource;
  const dsdViewToggle = isFinancialStatement ? null : (
    <DsdViewToggle
      active={showReconstructedDsd}
      onChange={handleDsdViewToggle}
    />
  );

  if (sections.length === 0) {
    const syntheticChunk = { ...chunk, dlViews: [dlView] };
    const rendererKey = `${chunk.id}:${dlViewIndex}:content`;
    // 목차 제목은 표 블록과 같은 제목 행으로 그린다(renderer 쪽 제목은 늘 감춘다).
    // 목차의 첫 블록만 제목을 갖는 규칙은 표 블록과 동일하다.
    const titleCellClass = cn(
      PANEL_SECTION_CLASS,
      BLOCK_TITLE_CELL_CLASS,
      isCardTop(cardEdge) && "rounded-t-lg border-t",
      isHeaderStuck && "rounded-lg border-b border-b-line-normal",
    );
    return (
      <div ref={scrollAnchorRef}>
        {isChunkStart && (
          <div
            ref={stickyHeaderRef}
            className={cn(
              compareCardClass(showWorkbook),
              "sticky top-[-1px] z-50",
            )}
          >
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 top-0 h-3 bg-background-gray-normal"
            />
            {showWorkbook && (
              <div
                aria-hidden
                className={titleCellClass}
                style={{ gridColumn: WORKBOOK_COL, gridRow: 1 }}
              />
            )}
            <div
              className={titleCellClass}
              style={{ gridColumn: IXD_COL, gridRow: 1 }}
            >
              <span className={CHUNK_TITLE_TEXT_CLASS}>{chunk.title}</span>
            </div>
          </div>
        )}
        <div className={compareCardClass(showWorkbook)}>
          {showWorkbook && (
            <div
              ref={sourceContainerRef}
              className={cn(
                PANEL_CARD_CLASS,
                // 표 블록과 같은 기준: 좌우 8. 아래는 다음 블록이 바로 이어지므로 12,
                // 카드의 마지막 블록일 때만 표 블록과 같은 20 으로 닫는다.
                "flex flex-col px-2 py-3",
                isCardTop(cardEdge) && !isChunkStart && "rounded-t-lg border-t",
                isCardBottom(cardEdge) && "rounded-b-lg border-b pb-5",
              )}
              style={{ gridColumn: WORKBOOK_COL, gridRow: 1 }}
            >
              {dsdViewToggle && (
                <div className="mb-3 flex justify-end">{dsdViewToggle}</div>
              )}
              <div className="min-h-0 flex-1">
                {showReconstructedDsd ? (
                  <ReconstructedDsdView
                    tables={reconstructedDsdTables}
                    isLoading={isDsdViewsLoading}
                    hasError={hasDsdViewsError}
                    showMissingMatchAlert={isMissingWorkbookMatch}
                  />
                ) : (
                  <MissingWorkbookPlaceholder
                    isFinancialStatement={isFinancialStatement}
                    onGoToFirstNote={onGoToFirstNote}
                    onShowDsdSource={showDsdSource}
                  />
                )}
              </div>
            </div>
          )}
          <div
            ref={targetContainerRef}
            className={cn(
              PANEL_CARD_CLASS,
              "min-w-0 overflow-clip px-2 py-3",
              // 제목은 위 제목 행이 그린다 — renderer 가 그리는 목차 제목은 감춘다
              "[&_[data-ixd-chunk-title]]:hidden",
              isCardTop(cardEdge) && !isChunkStart && "rounded-t-lg border-t",
              isCardBottom(cardEdge) && "rounded-b-lg border-b pb-5",
            )}
            style={{ gridColumn: IXD_COL, gridRow: 1 }}
          >
            <IxdPreviewRenderer
              ref={(renderer) => registerRowRenderer(rendererKey, renderer)}
              className="overflow-clip p-0"
              showBottomSpacer={false}
              chunks={[syntheticChunk]}
              renderScopeId={`${chunk.id}-compare-${dlViewIndex}-content`}
              sourceDlViewIndexOffset={dlViewIndex}
              editable
              cellChanges={session.cellChanges}
              changeOrigins={session.changeOrigins}
              cellIdentityByNormalizedCellId={
                session.cellIdentityByNormalizedCellId
              }
              identityChanges={session.identityChanges}
              cellNotices={cellNotices}
              identityLocationsByKey={session.identityLocationsByKey}
              // 워크북이 없는 IXD 값 편집기에서도 같은 문구를 적는다 — 셀 색·삼각형은
              // 거기서도 같은 뜻이고, 연동 셀 편집은 워크북 없이도 함께 바뀐다.
              showCellFootnotes
              onFocusIdentityLocation={focusIdentityLocation}
              chunkFsTypeById={session.chunkFsTypeById}
              onCellEdit={session.setCellValue}
              onCellEditBatch={session.setCellValues}
              showRawValueInEdit={session.showRawValueInEdit}
              getModifiedValues={getModifiedCellValues}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <WorkbookCellNoticeProvider
      notices={workbookCellNotices}
      noticesByTagKey={workbookCellNoticesByTagKey}
      onNoticeClick={handleWorkbookNoticeClick}
    >
      <div ref={scrollAnchorRef}>
        {failedCellCss && <style>{failedCellCss}</style>}
        {mixedScaleHeaderCss && <style>{mixedScaleHeaderCss}</style>}
        <style>{COPY_HINT_CSS}</style>
        {/*
         * 제목 행: 좌 = 워크북 표 이름 + 값 반영 버튼, 우 = IXD 목차/표 제목.
         * 카드 grid의 item은 sticky여도 자기 grid area 안에 갇히므로, 같은 컬럼
         * 템플릿의 별도 grid로 분리해 카드 전체 높이를 범위로 상단에 고정한다.
         * (표를 확인한 뒤 위로 올리지 않고 바로 값 반영을 누를 수 있게 한다)
         */}
        <div
          ref={stickyHeaderRef}
          className={cn(
            compareCardClass(showWorkbook),
            // 표의 sticky 가로 스크롤바(z-40)가 제목 위를 지나지 않도록 제목을
            // 이 페이지의 최상단 콘텐츠 레이어로 둔다.
            "sticky top-[-1px] z-50",
          )}
        >
          {/* 상단 라운드 바깥으로 표가 비치지 않게 하는 차폐 레이어. 하단은
            투명하게 남겨 둥근 제목 아래로 본문이 자연스럽게 이어지게 한다. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-3 bg-background-gray-normal"
          />
          {showWorkbook && (
            <div
              className={cn(
                PANEL_SECTION_CLASS,
                "relative flex min-h-14 items-center justify-between gap-3 px-5 py-3",
                isCardTop(cardEdge) && "rounded-t-lg border-t",
                isHeaderStuck && "rounded-lg border-b border-b-line-normal",
              )}
              style={{ gridColumn: WORKBOOK_COL, gridRow: 1 }}
            >
              {/* Figma: 값 반영 버튼이 표 이름 앞(= IXD 열 쪽)에 붙는다 */}
              <div className="flex shrink-0 items-center gap-3">
                {/* 재무제표 본문은 값 반영을 지원하지 않는다(WXBRL-2699) — 사유
                없이 비활성 버튼만 떠 있으면 고장으로 읽히므로 아예 걷어내고,
                안내는 워크북 칸의 플레이스홀더가 맡는다. 주석 목차는 워크북
                매칭 표가 없어도 렌더한다 — 매칭이 붙으면 바로 쓸 자리다. */}
                {!isFinancialStatement && (
                  <PasteMatchButton
                    targetContainerRef={targetContainerRef}
                    session={session}
                    workbookTableId={match?.tableId}
                    workbookRoleKey={match?.roleKey}
                    onPlanCoverageChange={setPlanCoverage}
                    onPasteResult={handlePasteResult}
                    applyLock={applyLock}
                    plan={plan}
                    notAppliedSummary={planNotAppliedSummary}
                    notAppliedCount={planDiagnostics.notAppliedCount}
                    mixedScaleWarning={mixedScaleWarning}
                    unitMismatchLines={unitMismatchLines}
                    periodEndNotice={periodEndNotice}
                    threeMonthAccMismatch={threeMonthAccMismatch}
                    taggedPeriodMismatch={taggedPeriodMismatchMessage}
                  />
                )}
              </div>
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <span className={cn("truncate", CHUNK_TITLE_TEXT_CLASS)}>
                  {withChunkNoteNumber(
                    chunk.title,
                    match?.tableName || dlView.title || chunk.title,
                  )}
                </span>
                {/*
                  매칭 실패 표식 (Figma 8873:793235). 사유 전문은 아래 빈 칸이
                  말하지만, 제목 줄은 sticky 라 표를 내려 읽는 동안에도 남는다 —
                  "이 칸이 왜 비었지"를 스크롤로 되찾지 않게 한다.
                  글로 쓴 사유가 같은 카드 안에 있으므로 이름 없는 아이콘은 숨긴다.
                */}
                {isMissingWorkbookMatch && (
                  <TriangleAlert
                    aria-hidden
                    className="size-4 shrink-0 text-status-error"
                  />
                )}
                <UnappliedValuesBadge
                  diagnostics={planDiagnostics}
                  groups={workbookUnappliedGroups}
                />
                {/* 반영불가 칩과 같은 자리를 배타적으로 쓴다 — 판정은
                  ValuesMatchStatus 안에 있다 */}
                <ValuesMatchStatus
                  coverageStatus={planCoverage.status}
                  notAppliedCount={planDiagnostics.notAppliedCount}
                  hasPasteFeedback={!!pasteFeedback}
                />
              </div>
              {dsdViewToggle}
            </div>
          )}
          <div
            className={cn(
              PANEL_SECTION_CLASS,
              BLOCK_TITLE_CELL_CLASS,
              isCardTop(cardEdge) && "rounded-t-lg border-t",
              isHeaderStuck && "rounded-lg border-b border-b-line-normal",
            )}
            style={{ gridColumn: IXD_COL, gridRow: 1 }}
          >
            <span className={CHUNK_TITLE_TEXT_CLASS}>
              {withChunkNoteNumber(chunk.title, dlView.title || chunk.title)}
            </span>
            {pasteFeedback && (
              <PasteFeedbackBadge
                hasFailures={activeFailedCellUniqueIds.length > 0}
              />
            )}
          </div>
        </div>

        {/* ponytail: 외부 grid로 비교격자와 DSD 오버레이를 같은 셀에 겹침.
          row 높이 = max(비교격자, DSD). DSD가 비교격자 track sizing에 관여 불가. */}
        <div className="grid">
          <div
            className={cn(
              compareCardClass(showWorkbook),
              "relative content-start",
            )}
            style={{ gridColumn: 1, gridRow: 1 }}
          >
            {/* ponytail: content-start 시 rows 아래 여백에도 IXD 카드 배경 채움 */}
            <div
              aria-hidden
              className={cn(
                "pointer-events-none absolute top-0 bottom-0 border-x border-line-normal bg-background-base",
                isCardTop(cardEdge) && "border-t",
                isCardBottom(cardEdge) && "rounded-b-lg border-b",
              )}
              style={{ left: 0, width: ixdPanelWidth(showWorkbook) }}
            />
            <div
              ref={targetContainerRef}
              className="contents"
              {...{ [MIXED_SCALE_ROW_SCOPE_ATTR]: rowKey }}
            >
              {sections.map((section, index) => {
                const metadataRow = index * 2 + 1;
                const tableRow = metadataRow + 1;
                if (!section.hasIxdTable) {
                  // relative: 카드 배경 레이어(absolute)보다 위에 그려지게 한다 —
                  // 표 칸과 같은 이유다. 빠지면 칸이 배경에 덮여 보이지 않는다.
                  return (
                    <div key={section.periodType} className="contents">
                      <div
                        className={cn(
                          PANEL_SECTION_CLASS,
                          "relative flex flex-col px-4 pb-1 pt-0",
                        )}
                        style={{ gridColumn: IXD_COL, gridRow: metadataRow }}
                      >
                        <div className={PERIOD_LABEL_ROW_CLASS}>
                          <span className="caption-2-medium text-label-alternative">
                            {section.workbookPeriodLabel}
                          </span>
                        </div>
                      </div>
                      <div
                        className={cn(
                          PANEL_SECTION_CLASS,
                          "relative px-2 pb-5 pt-0",
                        )}
                        style={{ gridColumn: IXD_COL, gridRow: tableRow }}
                      >
                        <MissingIxdPeriodPlaceholder
                          periodLabel={section.workbookPeriodLabel}
                          action={taggedPeriodMismatchAction ?? ""}
                        />
                      </div>
                    </div>
                  );
                }
                const renderScopeId = `${chunk.id}-compare-${dlViewIndex}-${section.periodType}`;
                const rendererKey = `${chunk.id}:${dlViewIndex}:${section.periodType}`;
                // 렌더러와 캡션이 같은 행을 보도록 한 곳에서만 뽑는다.
                const sectionRows = dlView.table?.[section.periodType] ?? [];
                const syntheticChunk: IxdChunk = {
                  ...chunk,
                  title: "",
                  dlViews: [
                    {
                      ...dlView,
                      title: "",
                      table: {
                        [section.periodType]: sectionRows,
                      } as IxdDlView["table"],
                    },
                  ],
                };
                return (
                  <div key={section.periodType} className="contents">
                    <div
                      className={cn(
                        PANEL_SECTION_CLASS,
                        "relative flex flex-col gap-0.5 px-4 pb-1 pt-0",
                      )}
                      style={{ gridColumn: IXD_COL, gridRow: metadataRow }}
                    >
                      <div className={cn(PERIOD_LABEL_ROW_CLASS, "gap-3")}>
                        <IxdPeriodLabel
                          label={section.periodLabel}
                          notice={periodEndNotice}
                        />
                        {threeMonthAcc && (
                          <ThreeMonthAccStatus
                            used={threeMonthAcc.ixd}
                            mismatch={threeMonthAccMismatch}
                          />
                        )}
                        {taggedPeriodMismatchAction &&
                          taggedPeriodMismatch?.missingInWorkbook.some(
                            (period) => period === section.periodType,
                          ) && (
                            <IxdExtraPeriodStatus
                              action={taggedPeriodMismatchAction}
                            />
                          )}
                      </div>
                      <TableUnitCaption
                        unitText={dlView.unitText}
                        foreignUnits={pickLineItemUnitsInRows(
                          dlView.lineItemUnits,
                          sectionRows,
                        )}
                        onMixedScaleWarningHover={setIsMixedScaleWarningHovered}
                      />
                    </div>

                    <div
                      className={cn(
                        PANEL_SECTION_CLASS,
                        // clip: hidden과 달리 scroll container를 만들지 않아
                        // 내부 표의 sticky 가로 스크롤바가 바깥 세로 스크롤러를 따라간다
                        // Figma 기준 여백: 좌우 8, 위 0, 아래 20.
                        // 표 아래 간격은 기간 사이든 블록 끝이든 20 으로 같고(라벨 칸은 위 여백 0),
                        // 그래서 카드 맨 아래만 좁아 보이는 일이 없다.
                        // renderer 기본 여백(px-5 pt-5)과 하단 스페이서는 아래에서 끈다.
                        "flex min-w-0 flex-col overflow-clip px-2 pb-5 pt-0",
                        "[&_[data-ixd-chunk-title]]:hidden [&_[data-ixd-dl-header]]:hidden [&_[data-ixd-period-label]]:hidden",
                        // 워크북과 나란히 볼 때만 표를 칸 너비에 맞춰 늘린다(좌우 행 높이 정렬).
                        // 워크북을 끄면 첫 진입 화면과 같은 natural 너비(table w-max)로 둔다 —
                        // 전폭에서 늘리면 셀이 과하게 벌어져 같은 표가 달라 보인다.
                        showWorkbook && "[&_table]:!w-full",
                      )}
                      style={{ gridColumn: IXD_COL, gridRow: tableRow }}
                    >
                      <IxdPreviewRenderer
                        ref={(renderer) =>
                          registerRowRenderer(rendererKey, renderer)
                        }
                        // 카드 안쪽 여백은 셀이 갖는다 — renderer 기본 px-5·pt-5 는 끈다
                        className="overflow-clip p-0"
                        showBottomSpacer={false}
                        chunks={[syntheticChunk]}
                        renderScopeId={renderScopeId}
                        sourceDlViewIndexOffset={dlViewIndex}
                        editable
                        cellChanges={session.cellChanges}
                        changeOrigins={session.changeOrigins}
                        cellIdentityByNormalizedCellId={
                          session.cellIdentityByNormalizedCellId
                        }
                        identityChanges={session.identityChanges}
                        cellNotices={cellNotices}
                        identityLocationsByKey={session.identityLocationsByKey}
                        showCellFootnotes
                        onFocusIdentityLocation={focusIdentityLocation}
                        chunkFsTypeById={session.chunkFsTypeById}
                        onCellEdit={session.setCellValue}
                        onCellEditBatch={session.setCellValues}
                        showRawValueInEdit={session.showRawValueInEdit}
                        showPeriodLabels={false}
                        getModifiedValues={getModifiedCellValues}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 워크북 셀. "DSD 원본 보기"를 켠 동안에는 자리만 지켜(invisible) 격자 행
              높이를 고정하고, 겹쳐 그리는 DSD 오버레이가 그 높이를 쓴다. */}
            {showWorkbook && (
              <div ref={sourceContainerRef} className="contents">
                {/*
                  매칭된 표가 하나도 없으면 기간별로 쪼개지 않고 칸 하나로 합친다.
                  기간마다 보여줄 표가 없는데 칸을 둘로 가르면, 같은 안내를 두 번
                  쌓거나(읽히지 않는다) 한쪽만 짧게 줄여야 한다(왜 다른지 설명할
                  수 없다). 기간 라벨도 빼는 게 맞다 — 가리킬 표가 없다.
                  기간별로 갈리는 것은 "매칭된 표에 그 기간 산출물이 있는지"뿐이고,
                  그건 아래 sections 경로가 칸마다 따로 그린다.
                */}
                {!match ? (
                  <div
                    className={cn(
                      PANEL_SECTION_CLASS,
                      "flex min-w-0 flex-col overflow-clip px-2 pb-5 pt-0",
                      isCardBottom(cardEdge) && "rounded-b-lg border-b",
                      showReconstructedDsd && "invisible",
                    )}
                    style={{
                      gridColumn: WORKBOOK_COL,
                      // 암묵 행이라 -1 을 못 쓴다 — 기간 하나당 라벨+표 두 행.
                      gridRow: `1 / span ${sections.length * 2}`,
                    }}
                  >
                    {/* 표 판정과 무관한 문제라 매칭이 없어도 알린다 */}
                    {hasBlockingPlanProblem && (
                      <div className="px-2 pb-1">
                        <PlanUnavailableAlert coverage={planCoverage} />
                      </div>
                    )}
                    <div className="min-h-0 flex-1">
                      <MissingWorkbookPlaceholder
                        isFinancialStatement={isFinancialStatement}
                        onGoToFirstNote={onGoToFirstNote}
                        onShowDsdSource={showDsdSource}
                      />
                    </div>
                  </div>
                ) : (
                  sections.map((section, index) => {
                    const metadataRow = index * 2 + 1;
                    const tableRow = metadataRow + 1;
                    return (
                      <div
                        key={`final-${section.periodType}`}
                        className="contents"
                      >
                        <div
                          className={cn(
                            PANEL_SECTION_CLASS,
                            "flex flex-col gap-0.5 px-4 pb-1 pt-0",
                            showReconstructedDsd && "invisible",
                          )}
                          style={{
                            gridColumn: WORKBOOK_COL,
                            gridRow: metadataRow,
                          }}
                        >
                          {/* 매칭 실패 안내는 아래 빈 칸(MissingTableMatchGuide) 안에서 말한다 */}
                          {hasBlockingPlanProblem && index === 0 && (
                            <PlanUnavailableAlert coverage={planCoverage} />
                          )}
                          <div
                            className={cn(
                              PERIOD_LABEL_ROW_CLASS,
                              "justify-between gap-2",
                            )}
                          >
                            <div className="flex min-w-0 items-center gap-3">
                              <span className="caption-2-medium text-label-normal">
                                {section.workbookPeriodLabel}
                              </span>
                              {/* current·prior 는 없는 기간도 빈 표로 온다 — 값이 비어
                                  보이는 이유를 표 위에서 바로 알 수 있게 한다. */}
                              {taggedPeriodMismatch?.missingInWorkbook.some(
                                (period) => period === section.periodType,
                              ) && (
                                <span className="caption-2-medium text-label-alternative">
                                  태깅 없음
                                </span>
                              )}
                              {threeMonthAcc && (
                                <ThreeMonthAccStatus
                                  used={threeMonthAcc.workbook}
                                />
                              )}
                            </div>
                            {index === 0 &&
                              match &&
                              (match.current ?? match.prior) && (
                                <WorkbookAutoSumToggle
                                  tableId={match.tableId}
                                  roleKey={match.roleKey}
                                  useAutoSum={
                                    (match.current ?? match.prior)!.useAutoSum
                                  }
                                  transpose={dlView.isTransposed}
                                />
                              )}
                          </div>
                          {section.finalTable && (
                            <TableUnitCaption
                              {...toTableUnitCaptionProps(section.finalTable)}
                            />
                          )}
                        </div>

                        <div
                          className={cn(
                            PANEL_SECTION_CLASS,
                            "overflow-clip px-2 pb-5 pt-0 [&_table]:!w-full",
                            index === sections.length - 1 &&
                              isCardBottom(cardEdge) &&
                              "rounded-b-lg border-b",
                            showReconstructedDsd && "invisible",
                          )}
                          style={{
                            gridColumn: WORKBOOK_COL,
                            gridRow: tableRow,
                          }}
                          data-copy-hint=""
                        >
                          {section.finalTable && section.finalPeriod ? (
                            <MatchedTablePeriodView
                              tablePreview={section.finalTable}
                              period={section.finalPeriod}
                              isError={false}
                            />
                          ) : (
                            <MatchedTableEmpty tableName={match.tableName} />
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* DSD 원본: 비교격자 바깥, 같은 외부 grid 셀에 겹침.
            비교격자 track sizing에 영향 0. 스크롤 없이 전부 표시.
            DSD가 더 길면 외부 grid row가 늘어나 IXD 아래에 여백 생김. */}
          {showReconstructedDsd && showWorkbook && (
            <div
              className={cn(
                PANEL_SECTION_CLASS,
                "flex flex-col overflow-clip",
                isCardBottom(cardEdge) && "rounded-b-lg border-b",
              )}
              style={{
                gridColumn: 1,
                gridRow: 1,
                // 워크북 패널이 오른쪽이라 겹침 오버레이도 오른쪽 절반에 붙인다
                justifySelf: "end",
                width: COMPARE_PANEL_WIDTH,
              }}
            >
              <ReconstructedDsdView
                tables={reconstructedDsdTables}
                isLoading={isDsdViewsLoading}
                hasError={hasDsdViewsError}
                showMissingMatchAlert={isMissingWorkbookMatch}
              />
            </div>
          )}
        </div>
        {workbookNoticePopover}
      </div>
    </WorkbookCellNoticeProvider>
  );
};

interface LazyCompareTableRowProps
  extends Omit<CompareTableRowProps, "isChunkStart"> {
  observeRow: ObserveCompareRow;
  /** 목차의 첫 행이면 chunk id — buildCompareRows가 목차당 하나만 배정한다 */
  anchorChunkId?: string;
  initiallyMounted: boolean;
  forceMounted: boolean;
  onForcedRowNearViewport: (rowKey: string) => void;
}

/** 모든 비교 행을 마운트용·유지용 IntersectionObserver 한 쌍으로 관찰한다. */
const useCompareRowObserver = (
  scrollContainerRef: React.RefObject<HTMLDivElement>,
): ObserveCompareRow => {
  const observersRef = useRef<IntersectionObserver[]>([]);
  const handlersRef = useRef(new Map<Element, CompareRowIntersectionHandler>());

  const observeRow = useCallback<ObserveCompareRow>((element, handler) => {
    handlersRef.current.set(element, handler);
    for (const observer of observersRef.current) observer.observe(element);
    return () => {
      for (const observer of observersRef.current) observer.unobserve(element);
      handlersRef.current.delete(element);
    };
  }, []);

  useEffect(() => {
    const root = scrollContainerRef.current;
    if (!root) return;

    const observers = [
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting)
              handlersRef.current.get(entry.target)?.("enterMountZone");
          }
        },
        { root, rootMargin: COMPARE_ROW_MOUNT_MARGIN },
      ),
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting)
              handlersRef.current.get(entry.target)?.("leaveKeepZone");
          }
        },
        { root, rootMargin: COMPARE_ROW_KEEP_MARGIN },
      ),
    ];
    observersRef.current = observers;
    for (const element of handlersRef.current.keys()) {
      for (const observer of observers) observer.observe(element);
    }

    return () => {
      for (const observer of observers) observer.disconnect();
      observersRef.current = [];
    };
  }, [scrollContainerRef]);

  return observeRow;
};

const LazyCompareTableRow = memo(function LazyCompareTableRow({
  observeRow,
  anchorChunkId,
  initiallyMounted,
  forceMounted,
  onForcedRowNearViewport,
  ...rowProps
}: LazyCompareTableRowProps) {
  const rowKey = buildRowKey(rowProps.chunk.id, rowProps.dlViewIndex);
  const handleNearViewport = useCallback(() => {
    if (forceMounted) onForcedRowNearViewport(rowKey);
  }, [forceMounted, onForcedRowNearViewport, rowKey]);
  const { rowRef, isMounted, placeholderStyle } = useVirtualizedCompareRow({
    observeRow,
    initiallyMounted,
    forceMounted,
    onNearViewport: handleNearViewport,
  });

  return (
    <div
      ref={rowRef}
      data-compare-chunk-id={anchorChunkId}
      style={placeholderStyle}
    >
      {isMounted ? (
        // 목차 앵커를 가진 행 = 그 목차의 첫 블록
        <CompareTableRow {...rowProps} isChunkStart={anchorChunkId != null} />
      ) : null}
    </div>
  );
});

/**
 * IXD에 짝이 없는 워크북 표.
 *
 * 짝이 없어도 계획에는 이 표의 실패가 들어 있다 — 오히려 "IXD에서 표를 찾지
 * 못했습니다"류가 몰리는 자리다. 매칭된 행과 같은 표식·같은 패널을 쓴다.
 */
const WorkbookOnlyTableRow = ({
  table,
  plan,
  cardEdge,
  chunkTitle,
}: {
  table: WorkbookMatchTable;
  plan: PlanAvailability;
  cardEdge: CardEdge;
  /** 이 표가 붙은 목차 — 표 이름에 목차 번호를 붙인다(목차 밖 꼬리 목록은 없음) */
  chunkTitle?: string;
}) => {
  const diagnostics = useMemo(
    () =>
      buildPlanTableDiagnostics(
        plan.status === "ready"
          ? plan.index.tablesByWorkbookTableId.get(table.tableId)
          : undefined,
      ),
    [plan, table.tableId],
  );
  const { notices, noticesByTagKey, unappliedGroups, onNoticeClick, popover } =
    useWorkbookCellNotices(diagnostics, table.tableName);

  const { reportQuarter } = useCompareTarget();
  const periods = [
    { key: "CURRENT" as const, table: table.current },
    { key: "PRIOR" as const, table: table.prior },
  ]
    .map((section) => ({
      ...section,
      label: getWorkbookPeriodLabel(
        section.key,
        reportQuarter,
        table.isPeriodEnd,
      ),
    }))
    .filter(
      (
        section,
      ): section is {
        key: "CURRENT" | "PRIOR";
        label: string;
        table: EditableTableItemsV2;
      } => !!section.table,
    );

  return (
    <WorkbookCellNoticeProvider
      notices={notices}
      noticesByTagKey={noticesByTagKey}
      onNoticeClick={onNoticeClick}
    >
      {/* 이 행은 showWorkbook 이 켜진 동안에만 마운트된다(LazyWorkbookOnlyTableRow) */}
      <style>{COPY_HINT_CSS}</style>
      <div className={compareCardClass(true)}>
        <div
          className={cn(
            PANEL_SECTION_CLASS,
            "flex items-center gap-2 px-4 pb-1 pt-4",
            isCardTop(cardEdge) && "rounded-t-lg border-t",
          )}
          style={{ gridColumn: WORKBOOK_COL, gridRow: 1 }}
        >
          <span className={cn("truncate", CHUNK_TITLE_TEXT_CLASS)}>
            {withChunkNoteNumber(chunkTitle ?? "", table.tableName)}
          </span>
          <UnappliedValuesBadge
            diagnostics={diagnostics}
            groups={unappliedGroups}
          />
        </div>
        <div
          className={cn(
            PANEL_SECTION_CLASS,
            "px-4 pb-1 pt-4",
            isCardTop(cardEdge) && "rounded-t-lg border-t",
          )}
          style={{ gridColumn: IXD_COL, gridRow: 1 }}
        >
          <span className={CHUNK_TITLE_TEXT_CLASS}>
            {withChunkNoteNumber(chunkTitle ?? "", table.tableName)}
          </span>
        </div>
        {periods.length === 0 ? (
          <>
            <div
              className={cn(
                PANEL_SECTION_CLASS,
                "min-h-[160px] px-2 pb-5 pt-0",
                isCardBottom(cardEdge) && "rounded-b-lg border-b",
              )}
              style={{ gridColumn: WORKBOOK_COL, gridRow: 2 }}
            >
              <MatchedTableEmpty tableName={table.tableName} />
            </div>
            <div
              className={cn(
                PANEL_SECTION_CLASS,
                "min-h-[160px] px-2 pb-5 pt-0",
                isCardBottom(cardEdge) && "rounded-b-lg border-b",
              )}
              style={{ gridColumn: IXD_COL, gridRow: 2 }}
            >
              <MissingTableMatchAlert sourceName="IXD" />
              <MissingTableMatchPlaceholder />
            </div>
          </>
        ) : (
          periods.map((section, index) => {
            const metadataRow = index * 2 + 2;
            const tableRow = metadataRow + 1;
            return (
              <div key={section.key} className="contents">
                <div
                  className={cn(
                    PANEL_SECTION_CLASS,
                    "flex flex-col gap-0.5 px-4 pb-1 pt-0",
                  )}
                  style={{ gridColumn: WORKBOOK_COL, gridRow: metadataRow }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="caption-2-medium text-label-normal">
                      {section.label}
                    </span>
                    {index === 0 && (
                      <WorkbookAutoSumToggle
                        tableId={table.tableId}
                        roleKey={table.roleKey}
                        useAutoSum={section.table.useAutoSum}
                        transpose={false}
                      />
                    )}
                  </div>
                  <TableUnitCaption
                    {...toTableUnitCaptionProps(section.table)}
                  />
                </div>
                <div
                  className={cn(
                    PANEL_SECTION_CLASS,
                    "flex flex-col gap-0.5 px-4 pb-1 pt-0",
                  )}
                  style={{ gridColumn: IXD_COL, gridRow: metadataRow }}
                >
                  {index === 0 && <MissingTableMatchAlert sourceName="IXD" />}
                  <span className="caption-2-medium text-label-normal">
                    {section.label}
                  </span>
                </div>
                <div
                  className={cn(
                    PANEL_SECTION_CLASS,
                    // clip: 내부 표의 sticky 가로 스크롤바가 바깥 스크롤러를 따라가도록
                    "overflow-clip px-2 pb-5 pt-0 [&_table]:!w-full",
                    index === periods.length - 1 &&
                      isCardBottom(cardEdge) &&
                      "rounded-b-lg border-b",
                  )}
                  style={{ gridColumn: WORKBOOK_COL, gridRow: tableRow }}
                  // IXD 대응 표가 없는 행이라 수동 복사가 유일한 경로다.
                  data-copy-hint=""
                >
                  <MatchedTablePeriodView
                    tablePreview={section.table}
                    period={section.key}
                    isError={false}
                  />
                </div>
                <div
                  className={cn(
                    PANEL_SECTION_CLASS,
                    "overflow-hidden px-2 pb-5 pt-0",
                    index === periods.length - 1 &&
                      isCardBottom(cardEdge) &&
                      "rounded-b-lg border-b",
                  )}
                  style={{ gridColumn: IXD_COL, gridRow: tableRow }}
                >
                  <MissingTableMatchPlaceholder />
                </div>
              </div>
            );
          })
        )}
      </div>
      {popover}
    </WorkbookCellNoticeProvider>
  );
};

/**
 * 표도 문단도 없는 목차.
 *
 * 행을 하나도 그리지 않으면 목차를 눌러도 갈 앵커가 없어 아무 일도 일어나지 않고,
 * 문서에 그 목차가 있다는 사실조차 화면에서 사라진다. 자리와 사유를 함께 남긴다.
 */
const EmptyChunkRow = ({
  chunk,
  anchorChunkId,
  cardEdge,
  showWorkbook,
}: {
  chunk: IxdChunk;
  anchorChunkId: string;
  cardEdge: CardEdge;
  showWorkbook: boolean;
}) => {
  const cellClass = cn(
    PANEL_SECTION_CLASS,
    "flex min-h-14 flex-col justify-center gap-0.5 px-5 py-3",
    isCardTop(cardEdge) && "rounded-t-lg border-t",
    isCardBottom(cardEdge) && "rounded-b-lg border-b",
  );

  return (
    <div
      data-compare-chunk-id={anchorChunkId}
      className={compareCardClass(showWorkbook)}
    >
      <div className={cellClass} style={{ gridColumn: IXD_COL, gridRow: 1 }}>
        <span className={CHUNK_TITLE_TEXT_CLASS}>{chunk.title}</span>
        <span className="caption-2-regular text-label-alternative">
          이 목차에는 표가 없습니다.
        </span>
      </div>
      {showWorkbook && (
        <div
          aria-hidden
          className={cellClass}
          style={{ gridColumn: WORKBOOK_COL, gridRow: 1 }}
        />
      )}
    </div>
  );
};

const LazyWorkbookOnlyTableRow = memo(function LazyWorkbookOnlyTableRow({
  table,
  plan,
  observeRow,
  anchorChunkId,
  initiallyMounted = false,
  showWorkbook,
  cardEdge,
  chunkTitle,
}: {
  table: WorkbookMatchTable;
  plan: PlanAvailability;
  observeRow: ObserveCompareRow;
  chunkTitle?: string;
  /** 목차에 IXD 표가 없어 이 워크북 표가 목차 앵커를 맡을 때 — buildCompareRows가 배정 */
  anchorChunkId?: string;
  initiallyMounted?: boolean;
  showWorkbook: boolean;
  cardEdge: CardEdge;
}) {
  const { rowRef, isMounted, placeholderStyle } = useVirtualizedCompareRow({
    observeRow,
    initiallyMounted,
  });

  // 이 행은 IXD 칸에도 "매칭 없음" 안내만 있다 — 스위치를 끄면 높이까지 접는다.
  // invisible 로만 감추면 아무것도 없는 빈 공간이 남고 보이지 않는 DSD 표를 계속 마운트한다.
  // 대신 자리는 남긴다(display:none 아님) — 목차 앵커(data-compare-chunk-id)가 여기 붙어 있어
  // 사라지면 목차 클릭이 좌표를 잃는다. 접히며 생기는 이동은 토글 후 재정렬이 맡는다.
  return (
    <div
      ref={rowRef}
      data-compare-chunk-id={anchorChunkId}
      className={cn(!showWorkbook && "h-0 overflow-hidden")}
      style={showWorkbook ? placeholderStyle : undefined}
    >
      {showWorkbook && isMounted ? (
        <WorkbookOnlyTableRow
          table={table}
          plan={plan}
          cardEdge={cardEdge}
          chunkTitle={chunkTitle}
        />
      ) : null}
    </div>
  );
});

const CompareTableRowsComponent = ({
  workbookId,
  reportQuarter,
  reportId,
  chunks,
  matches,
  workbookOnly,
  workbookOnlyByChunkId,
  session,
  scrollContainerRef,
  onScroll,
  onUndo,
  focusRequest,
  getModifiedCellValues,
  rejectedCellNotices,
  getReconstructedDsdTables,
  isDsdViewsLoading,
  hasDsdViewsError,
  onRequestDsdViews,
  plan,
  showWorkbook,
  onGoToFirstNote,
}: CompareTableRowsProps) => {
  const observeRow = useCompareRowObserver(scrollContainerRef);
  const rendererRefs = useRef(new Map<string, IxdPreviewRendererRef>());
  const pendingFocusRequestRef = useRef<{
    location: CellIdentityLocation;
    scrollBehavior?: ScrollBehavior;
  } | null>(null);
  const [forceMountedRowKey, setForceMountedRowKey] = useState<string | null>(
    null,
  );
  const [pasteFeedbackHistory, setPasteFeedbackHistory] =
    useState<PasteFeedbackHistory>(() => new Map());
  const registerRenderer = useCallback(
    (key: string, renderer: IxdPreviewRendererRef | null) => {
      if (renderer) {
        rendererRefs.current.set(key, renderer);
        const pendingRequest = pendingFocusRequestRef.current;
        if (
          pendingRequest &&
          renderer.focusCellLocation(pendingRequest.location, {
            scrollBehavior: pendingRequest.scrollBehavior,
          })
        ) {
          pendingFocusRequestRef.current = null;
        }
      } else {
        rendererRefs.current.delete(key);
      }
    },
    [],
  );
  const focusIdentityLocation = useCallback(
    (
      location: CellIdentityLocation,
      scrollBehavior?: ScrollBehavior,
    ): boolean => {
      const rowKey = buildRowKey(location.chunkId, location.dlViewIndex);
      for (const renderer of rendererRefs.current.values()) {
        if (renderer.focusCellLocation(location, { scrollBehavior })) {
          pendingFocusRequestRef.current = null;
          // 이동이 중단된 먼 행을 다시 선택한 경우에는 관찰 범위에 도착할 때까지
          // 강제 마운트를 유지한다. 다른 행으로 이동할 때만 이전 강제 행을 해제한다.
          setForceMountedRowKey((current) =>
            current === rowKey ? current : null,
          );
          return true;
        }
      }
      pendingFocusRequestRef.current = { location, scrollBehavior };
      setForceMountedRowKey(rowKey);
      return true;
    },
    [],
  );
  const releaseForcedRow = useCallback((rowKey: string) => {
    setForceMountedRowKey((current) => (current === rowKey ? null : current));
  }, []);

  // 되돌리기 등 외부 요청으로 해당 셀을 선택·중앙 스크롤한다. 대상 행이 아직
  // 마운트되지 않았으면 focusIdentityLocation이 강제 마운트 후 포커스를 이어간다.
  useEffect(() => {
    if (focusRequest) {
      focusIdentityLocation(focusRequest.location, focusRequest.scrollBehavior);
    }
  }, [focusIdentityLocation, focusRequest]);

  const rows = useMemo<CompareRow[]>(
    () => buildCompareRows(chunks, matches, workbookOnlyByChunkId),
    [chunks, matches, workbookOnlyByChunkId],
  );

  /**
   * 블록을 한 카드로 잇기 위한 계산 — 보이는 블록 기준이다.
   *
   * 스위치가 꺼지면 워크북 전용 행은 높이 0 으로 접히므로(LazyWorkbookOnlyTableRow)
   * 그 자리에 띠를 넣으면 띠만 두 겹으로 남는다. 접힌 행은 건너뛰고 띠를 넣는다.
   */
  const { dividerBefore, rowCardEdges } = useMemo(() => {
    const visible = rows.map((row) => showWorkbook || row.kind !== "workbook");
    const first = visible.indexOf(true);
    const last = visible.lastIndexOf(true);
    let seen = false;
    const divider = rows.map((_, index) => {
      if (!visible[index]) return false;
      const needsDivider = seen;
      seen = true;
      return needsDivider;
    });
    return {
      dividerBefore: divider,
      rowCardEdges: rows.map((_, index) => cardEdgeAt(index, first, last)),
    };
  }, [rows, showWorkbook]);
  const undoCount = session.undoCount;
  const handlePasteResult = useCallback(
    (rowKey: string, result: PasteMatchResult) => {
      setPasteFeedbackHistory((current) =>
        appendPasteFeedback(current, rowKey, {
          ...result,
          undoDepth: undoCount + 1,
        }),
      );
    },
    [undoCount],
  );

  const handleApplyLockRelease = useCallback((rowKey: string) => {
    setPasteFeedbackHistory((current) => releaseApplyLock(current, rowKey));
  }, []);
  const handleUnitNoticeOpened = useCallback((rowKey: string) => {
    setPasteFeedbackHistory((current) => markUnitNoticeOpened(current, rowKey));
  }, []);

  useEffect(() => {
    setPasteFeedbackHistory((current) =>
      trimPasteFeedbackHistory(current, undoCount),
    );
  }, [undoCount]);

  // 표 복사 버튼은 셀 selection을 거치지 않으므로 renderer별 단축키 소유권에
  // 기대면 Ctrl+Z가 누락된다. 비교 화면에서 하나만 등록해 모든 편집 그룹을 되돌린다.
  useEffect(() => {
    const handleUndo = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      if (
        !(event.ctrlKey || event.metaKey) ||
        event.code !== "KeyZ" ||
        event.shiftKey ||
        undoCount === 0
      ) {
        return;
      }
      event.preventDefault();
      onUndo();
    };
    document.addEventListener("keydown", handleUndo);
    return () => document.removeEventListener("keydown", handleUndo);
  }, [onUndo, undoCount]);

  const body = (
    <div className="flex min-h-0 flex-1 flex-col bg-background-gray-normal">
      {/* 파일 이름 줄은 화면 상단 SubHeader 가 맡는다 (Figma 7145:461461) */}
      <div
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-auto"
        onScroll={(event) => onScroll(event.currentTarget)}
      >
        <div className="flex flex-col gap-3 px-4 py-3">
          {/* 블록들은 카드 하나 안에서 12px 띠로만 나뉜다 (Figma 7145:461461) */}
          <div className="flex flex-col">
            {rows.map((row, index) =>
              row.kind === "empty" ? (
                <Fragment key={row.key}>
                  {dividerBefore[index] && (
                    <CompareCardDivider showWorkbook={showWorkbook} />
                  )}
                  <EmptyChunkRow
                    chunk={row.chunk}
                    anchorChunkId={row.anchorChunkId}
                    cardEdge={rowCardEdges[index]}
                    showWorkbook={showWorkbook}
                  />
                </Fragment>
              ) : row.kind === "workbook" ? (
                <Fragment key={row.key}>
                  {dividerBefore[index] && (
                    <CompareCardDivider showWorkbook={showWorkbook} />
                  )}
                  <LazyWorkbookOnlyTableRow
                    table={row.table}
                    plan={plan}
                    observeRow={observeRow}
                    chunkTitle={row.chunkTitle}
                    anchorChunkId={row.anchorChunkId}
                    initiallyMounted={index < 3}
                    showWorkbook={showWorkbook}
                    cardEdge={rowCardEdges[index]}
                  />
                </Fragment>
              ) : (
                <Fragment key={row.key}>
                  {dividerBefore[index] && (
                    <CompareCardDivider showWorkbook={showWorkbook} />
                  )}
                  <LazyCompareTableRow
                    anchorChunkId={row.anchorChunkId}
                    chunk={row.chunk}
                    dlView={row.dlView}
                    dlViewIndex={row.dlViewIndex}
                    match={row.match}
                    session={session}
                    pasteFeedback={getActivePasteFeedback(
                      pasteFeedbackHistory,
                      row.key,
                    )}
                    onPasteResult={handlePasteResult}
                    onApplyLockRelease={handleApplyLockRelease}
                    onUnitNoticeOpened={handleUnitNoticeOpened}
                    plan={plan}
                    registerRenderer={registerRenderer}
                    focusIdentityLocation={focusIdentityLocation}
                    getModifiedCellValues={getModifiedCellValues}
                    rejectedCellNotices={rejectedCellNotices}
                    scrollContainerRef={scrollContainerRef}
                    getReconstructedDsdTables={getReconstructedDsdTables}
                    isDsdViewsLoading={isDsdViewsLoading}
                    hasDsdViewsError={hasDsdViewsError}
                    onRequestDsdViews={onRequestDsdViews}
                    showWorkbook={showWorkbook}
                    onGoToFirstNote={onGoToFirstNote}
                    observeRow={observeRow}
                    initiallyMounted={index < 3}
                    forceMounted={forceMountedRowKey === row.key}
                    onForcedRowNearViewport={releaseForcedRow}
                    cardEdge={rowCardEdges[index]}
                  />
                </Fragment>
              ),
            )}
          </div>
          {/* 목차 앵커가 없는 꼬리 목록의 제목 — 접은 행 위에 덩그러니 남지 않게 함께 빼낸다 */}
          {showWorkbook && workbookOnly.length > 0 && (
            <div className="px-2 pt-3 label-1-normal-semibold text-label-alternative">
              IXD 목차에 없는 워크북 표
            </div>
          )}
          <div className="flex flex-col">
            {workbookOnly.map((table, index) => (
              <Fragment key={table.tableId}>
                {index > 0 && showWorkbook && (
                  <CompareCardDivider showWorkbook={showWorkbook} />
                )}
                <LazyWorkbookOnlyTableRow
                  table={table}
                  plan={plan}
                  observeRow={observeRow}
                  showWorkbook={showWorkbook}
                  cardEdge={cardEdgeAt(index, 0, workbookOnly.length - 1)}
                />
              </Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // 합계 자동계산 스위치·기간 라벨은 여러 홉 아래에 있다 — 캐시를 짚을 id 와 보고 기간은
  // context 로 내린다.
  return (
    <CompareTargetProvider
      workbookId={workbookId}
      reportQuarter={reportQuarter}
      reportId={reportId}
    >
      {body}
    </CompareTargetProvider>
  );
};

const areCompareTableRowsPropsEqual = (
  previous: CompareTableRowsProps,
  next: CompareTableRowsProps,
) => {
  const previousKeys = Object.keys(previous) as (keyof CompareTableRowsProps)[];
  const nextKeys = Object.keys(next) as (keyof CompareTableRowsProps)[];
  if (previousKeys.length !== nextKeys.length) return false;

  for (const key of previousKeys) {
    if (key === "session") continue;
    if (!Object.is(previous[key], next[key])) return false;
  }

  return COMPARE_TABLE_SESSION_KEYS.every((key) =>
    Object.is(previous.session[key], next.session[key]),
  );
};

export const CompareTableRows = memo(
  CompareTableRowsComponent,
  areCompareTableRowsPropsEqual,
);
